import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, group, Result, ResultValue, ResultError } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { validateExpectNumber, validateRequire, validateStringLength } from 'src/api/utils/formValidators';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import { PromoNotificationsModelType } from 'src/api/config/promo_notifications/get_promo_notifications';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createPromoNotification';
import { UploadImageState } from '../UploadImageState/UploadImageState';

type PromoNotificationsBodyType = ParamsType['body'];
interface PromoNotificationsLoading {
    type: 'loading';
}

interface PromoNotificationsReady {
    type: 'ready';
    value: Array<PromoNotificationsModelType>;
}

interface PromoNotificationsError {
    type: 'error';
    value: string;
}

type PromoNotificationsContentResponse = PromoNotificationsLoading | PromoNotificationsReady | PromoNotificationsError;
type PageType = 'edit' | 'add';

interface PromoNotificationsType {
    title: string;
    HTMLUrl: string;
    dateFrom: string;
    dateTo: string;
    ctaText: string;
    ctaUrl: string;
    eventId: number | null;
    competitionId: number | null;
    casinoGameId: number | null;
    sport: string;
    tcText: string;
    isActive: boolean;
    mediaType: string;
    actionTemplate: string;
}

interface NotificationPageVideoType {
    title: string;
    dateFrom: string;
    dateTo: string;
    videoUrl: string;
}

export interface CompetitionSportType {
    competitionId: number | null;
    sport: string;
}
export class NotificationPageState {
    @observable private readonly promoNotificationsResource: Resource<Array<PromoNotificationsModelType>>;
    @observable private page: string = 'add';
    @observable private isActiveFlagChanged: boolean = false;
    @observable public editedNotificationPage: PromoNotificationsModelType | null = null;
    @observable public isLoading: boolean = false;
    @observable public errorMessage: string = '';
    @observable public selectedNotificationPageType: 'image' | 'video' = 'image';

    @observable onGoingUploadFile: File | null = null;

    readonly titleModel: FormModel<string>;
    readonly title: FormInputState<string>;
    readonly titleStatus: InputStatus;
    readonly HTMLUrlModel: FormModel<string>;
    readonly HTMLUrl: FormInputState<string>;
    readonly imgUrlModel: FormModel<string>;
    readonly imgUrl: FormInputState<string>;
    readonly videoUrlModel: FormModel<string>;
    readonly videoUrl: FormInputState<string>;
    readonly videoUrlStatus: InputStatus;
    readonly ctaTextModel: FormModel<string>;
    readonly ctaText: FormInputState<string>;
    readonly ctaTextStatus: InputStatus;
    readonly ctaUrlModel: FormModel<string>;
    readonly ctaUrl: FormInputState<string>;
    readonly ctaUrlStatus: InputStatus;
    readonly eventIdModel: FormModel<number | null>;
    readonly eventId: FormInputState<string>;
    readonly eventIdStatus: InputStatus;
    readonly competitionIdModel: FormModel<number | null>;
    readonly competitionId: FormInputState<string>;
    readonly competitionIdStatus: InputStatus;
    readonly casinoGameIdModel: FormModel<number | null>;
    readonly casinoGameId: FormInputState<string>;
    readonly casinoGameIdStatus: InputStatus;
    readonly sportModel: FormModel<string>;
    readonly sport: FormInputState<string>;
    readonly sportStatus: InputStatus;
    readonly tcTextModel: FormModel<string>;
    readonly tcText: FormInputState<string>;
    readonly tcTextStatus: TextareaStatus;
    readonly isActiveModel: FormModel<boolean>;
    readonly isActive: FormInputState<boolean>;
    readonly isActiveStatus: InputStatus;
    readonly dateFromModel: FormModel<string>;
    readonly dateFrom: FormInputState<string>;
    readonly dateFromStatus: InputStatus;
    readonly dateToModel: FormModel<string>;
    readonly dateTo: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly actionTemplateModel: FormModel<string>;
    readonly actionTemplate: FormInputState<string>;
    readonly actionTemplateStatus: InputStatus;
    readonly mediaModel: FormModel<string>;
    readonly mediaType: FormInputState<string>;
    readonly mediaStatus: InputStatus;
    readonly formForImg: FormModel<PromoNotificationsType>;
    readonly formForVideo: FormModel<NotificationPageVideoType>;
    readonly competitionSport: FormModel<CompetitionSportType>;

    public uploadImageState: UploadImageState;

    constructor() {
        this.uploadImageState = new UploadImageState('notifications-promo');

        this.promoNotificationsResource = new Resource(async (): Promise<Array<PromoNotificationsModelType>> => {
            this.isLoading = true;
            const response = await apiCommon.get_promo_notifications.run({});
            this.isLoading = false;
            return response;
        });

        this.title = new FormInputState('', false);
        this.titleModel = this.title.map(validateRequire).map(validateStringLength);
        this.titleStatus = new InputStatus(this.titleModel);

        this.HTMLUrl = new FormInputState('', false);
        this.HTMLUrlModel = this.HTMLUrl.toModel();

        this.imgUrl = new FormInputState('', false);
        this.imgUrlModel = this.imgUrl.toModel();

        this.videoUrl = new FormInputState('', false);
        this.videoUrlModel = this.videoUrl.map(validateRequire);
        this.videoUrlStatus = new InputStatus(this.videoUrlModel);

        this.ctaText = new FormInputState('', false);
        this.ctaTextModel = this.ctaText.toModel();
        this.ctaTextStatus = new InputStatus(this.ctaTextModel);

        this.ctaUrl = new FormInputState('', false);
        this.ctaUrlModel = this.ctaUrl.toModel();
        this.ctaUrlStatus = new InputStatus(this.ctaUrlModel);

        this.eventId = new FormInputState('', false);
        this.eventIdModel = this.eventId.map(validateExpectNumber);
        this.eventIdStatus = new InputStatus(this.eventIdModel);

        this.competitionId = new FormInputState('', false);
        this.competitionIdModel = this.competitionId.map(validateExpectNumber);
        this.competitionIdStatus = new InputStatus(this.competitionIdModel);

        this.casinoGameId = new FormInputState('', false);
        this.casinoGameIdModel = this.casinoGameId.map(validateExpectNumber);
        this.casinoGameIdStatus = new InputStatus(this.casinoGameIdModel);

        this.sport = new FormInputState('', false);
        this.sportModel = this.sport.toModel();
        this.sportStatus = new InputStatus(this.sportModel);

        this.actionTemplate = new FormInputState('direct-link', false);
        this.actionTemplateModel = this.actionTemplate.map(validateRequire);
        this.actionTemplateStatus = new InputStatus(this.actionTemplateModel);

        this.mediaType = new FormInputState('static', false);
        this.mediaModel = this.mediaType.toModel();
        this.mediaStatus = new InputStatus(this.mediaModel);

        this.tcText = new FormInputState('', false);
        this.tcTextModel = this.tcText.toModel();
        this.tcTextStatus = new TextareaStatus(this.tcTextModel);

        this.dateFrom = new FormInputState('', false);
        this.dateFromModel = this.dateFrom.map(validateRequire);
        this.dateFromStatus = new InputStatus(this.dateFromModel);

        this.dateTo = new FormInputState('', false);
        this.dateToModel = this.dateTo.map(validateRequire);
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.isActive = new FormInputState(false, false);
        this.isActiveModel = this.isActive.toModel();
        this.isActiveStatus = new InputStatus(this.isActiveModel);

        this.competitionSport = group({
            competitionId: this.competitionIdModel,
            sport: this.sportModel,
        }).map((item: CompetitionSportType): Result<CompetitionSportType> => {
            if (item.competitionId === null || item.sport === '') {
                return new ResultError('Both values needed');
            }

            return new ResultValue({
                competitionId: item.competitionId,
                sport: item.sport,
            });
        });

        this.formForImg = group({
            title: this.titleModel,
            HTMLUrl: this.HTMLUrlModel,
            ctaText: this.ctaTextModel,
            ctaUrl: this.ctaUrlModel,
            eventId: this.eventIdModel,
            competitionId: this.competitionIdModel,
            casinoGameId: this.casinoGameIdModel,
            sport: this.sport,
            tcText: this.tcTextModel,
            isActive: this.isActiveModel,
            dateFrom: this.dateFromModel,
            dateTo: this.dateToModel,
            mediaType: this.mediaModel,
            actionTemplate: this.actionTemplate,
        }).map((item: PromoNotificationsType): Result<PromoNotificationsType> => {
            return new ResultValue({
                title: item.title,
                HTMLUrl: item.HTMLUrl,
                dateFrom: item.dateFrom,
                dateTo: item.dateTo,
                ctaText: item.ctaText,
                ctaUrl: item.ctaUrl,
                eventId: item.eventId,
                competitionId: item.competitionId,
                casinoGameId: item.casinoGameId,
                sport: item.sport,
                tcText: item.tcText,
                isActive: item.isActive,
                mediaType: item.mediaType,
                actionTemplate: item.actionTemplate,
            });
        });

        this.formForVideo = group({
            title: this.titleModel,
            dateFrom: this.dateFromModel,
            dateTo: this.dateToModel,
            videoUrl: this.videoUrlModel,
        }).map((item: NotificationPageVideoType): Result<NotificationPageVideoType> => {
            return new ResultValue({
                title: item.title,
                dateFrom: item.dateFrom,
                dateTo: item.dateTo,
                videoUrl: item.videoUrl,
            });
        });
        makeAutoObservable(this);
    }

    @action public handleCancel = (): void => {
        if (this.isEditMode && this.editedNotificationPage !== null) {
            this.setEditedNotificationPage(this.editedNotificationPage);
        } else {
            this.formForImg.reset();
            this.formForVideo.reset();
        }
    };

    @action public resetForm = (): void => {
        this.setPage('add');
        this.editedNotificationPage = null;
        this.formForImg.reset();
        this.formForVideo.reset();
    };

    @action public setPage = (value: PageType): void => {
        this.page = value;
    };

    @action public setActiveFlag = async (promoNotification: PromoNotificationsModelType) => {
        this.isActiveFlagChanged = true;
        this.isLoading = true;

        const payload: PromoNotificationsModelType = {
            ...promoNotification,
            is_active: !promoNotification.is_active,
        };

        const submitResult = await this.updatePromoNotification(payload, promoNotification.id);

        if (submitResult) {
            await this.promoNotificationsResource.refresh();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
        this.isActiveFlagChanged = false;
        this.isLoading = false;
    };

    @computed public get isHTMLBannerType(): boolean {
        return this.mediaType.value === 'html';
    }

    @action public onRemove = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.delete_promo_notifications.run({ id });

        if (removeResult) {
            await this.promoNotificationsResource.refresh();

            this.formForImg.reset();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @computed public get competitionSportValue(): CompetitionSportType | null {
        const result = this.competitionSport.result;

        if (result instanceof ResultValue) {
            return {
                competitionId: result.value.competitionId,
                sport: result.value.sport,
            };
        }

        return null;
    }

    @action public handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.formForImg.setAsVisited();
        const result = this.formForImg.result;

        const topElem = this.notificationPagesSorted[0] ?? null;
        const display_order =
            this.isEditMode && this.editedNotificationPage !== null
                ? this.editedNotificationPage.display_order
                : topElem !== null
                  ? topElem.display_order + 1
                  : 1;

        if (result instanceof ResultValue) {
            const additionalPayload =
                result.value.actionTemplate === 'competition-id' ? this.competitionSportValue : null;

            if (result.value.actionTemplate === 'competition-id' && additionalPayload === null) {
                this.isLoading = false;
                return;
            }

            const payload: PromoNotificationsBodyType = {
                title: result.value.title,
                html_url: result.value.HTMLUrl,
                img_url: this.uploadImageState.imgUrl,
                video_url: '',
                date_from: result.value.dateFrom,
                date_to: result.value.dateTo,
                cta_text: result.value.ctaText,
                cta_url: result.value.ctaUrl,
                event_id: result.value.eventId,
                competition_id:
                    additionalPayload !== null ? additionalPayload.competitionId : result.value.competitionId,
                casino_game_id: result.value.casinoGameId,
                sport: additionalPayload !== null ? additionalPayload.sport : result.value.sport,
                tc_text: result.value.tcText,
                is_active: result.value.isActive,
                media_type: result.value.mediaType,
                action_template: result.value.actionTemplate,
                display_order: display_order,
            };

            const submitResult =
                this.isEditMode && this.editedNotificationPage !== null
                    ? await this.updatePromoNotification(payload, this.editedNotificationPage.id)
                    : await this.createPromoNotification(payload);

            if (submitResult) {
                await this.promoNotificationsResource.refresh();
                this.resetForm();
                this.uploadImageState.handleResetImage();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action public handleSubmitVideo = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.formForVideo.setAsVisited();
        const result = this.formForVideo.result;

        const topElem = this.notificationPagesSorted[0] ?? null;
        const display_order =
            this.isEditMode && this.editedNotificationPage !== null
                ? this.editedNotificationPage.display_order
                : topElem !== null
                  ? topElem.display_order + 1
                  : 1;

        if (result instanceof ResultValue) {
            const payload: PromoNotificationsBodyType = {
                title: result.value.title,
                html_url: '',
                img_url: '',
                video_url: result.value.videoUrl,
                date_from: result.value.dateFrom,
                date_to: result.value.dateTo,
                cta_text: '',
                cta_url: '',
                event_id: null,
                competition_id: null,
                casino_game_id: null,
                sport: '',
                tc_text: '',
                is_active: this.editedNotificationPage?.is_active ?? false,
                media_type: 'video',
                action_template: '',
                display_order: display_order,
            };

            const submitResult =
                this.isEditMode && this.editedNotificationPage !== null
                    ? await this.updatePromoNotification(payload, this.editedNotificationPage.id)
                    : await this.createPromoNotification(payload);

            if (submitResult) {
                await this.promoNotificationsResource.refresh();
                this.resetForm();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action private createPromoNotification = async (
        promoNotification: PromoNotificationsBodyType
    ): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            response = await apiCommon.post_create_promo_notifications.run({ data: promoNotification });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private updatePromoNotification = async (
        promoNotification: PromoNotificationsBodyType,
        id: number
    ): Promise<number | Error | null> => {
        let response: number | Error | null;

        try {
            response = await apiCommon.put_update_promo_notifications.run({ data: promoNotification, id: id });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action public setEditedNotificationPage = (promoNotification: PromoNotificationsModelType) => {
        if (promoNotification.media_type === 'video') {
            this.selectFilterVideo();
        } else {
            this.selectFilterImage();
        }

        this.uploadImageState.handleChooseImage(promoNotification.img_url ?? '');
        this.editedNotificationPage = promoNotification;
        if (this.landingPages.type === 'ready') {
            const eventId = promoNotification.event_id ?? '';
            const competitionId = promoNotification.competition_id ?? '';
            const casinoGameId = promoNotification.casino_game_id ?? '';

            if (promoNotification !== null && promoNotification !== undefined) {
                this.title.setValue(promoNotification.title);
                this.imgUrl.setValue(promoNotification.img_url ?? '');
                this.HTMLUrl.setValue(promoNotification.html_url ?? '');
                this.videoUrl.setValue(promoNotification.video_url ?? '');
                this.dateFrom.setValue(promoNotification.date_from);
                this.dateTo.setValue(promoNotification.date_to);
                this.ctaText.setValue(promoNotification.cta_text);
                this.ctaUrl.setValue(promoNotification.cta_url ?? '');
                this.eventId.setValue(eventId.toString());
                this.competitionId.setValue(competitionId.toString());
                this.casinoGameId.setValue(casinoGameId.toString());
                this.sport.setValue(promoNotification.sport ?? '');
                this.tcText.setValue(promoNotification.tc_text);
                this.isActive.setValue(promoNotification.is_active);
                this.actionTemplate.setValue(promoNotification.action_template);
                this.mediaType.setValue(promoNotification.media_type);
            }
        }
    };

    @computed private get landingPages(): PromoNotificationsContentResponse {
        const landingPage = this.promoNotificationsResource.get();
        if (landingPage.type === 'ready' && landingPage.value !== null) {
            return {
                type: 'ready',
                value: landingPage.value,
            };
        }
        if (landingPage.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed public get notificationPagesSorted(): Array<PromoNotificationsModelType> {
        if (this.landingPages.type === 'ready') {
            return this.landingPages.value.sort(
                (lp1: PromoNotificationsModelType, lp2: PromoNotificationsModelType) => {
                    if (lp1 !== null && lp2 !== null) {
                        if (lp1.display_order > lp2.display_order) {
                            return -1;
                        }
                        if (lp1.display_order < lp2.display_order) {
                            return 1;
                        }
                        return 0;
                    }
                    return -1;
                }
            );
        }
        return [];
    }

    @computed public get isFormValid(): boolean {
        return this.formForImg.result instanceof ResultValue;
    }

    @computed public get isFormValidVideo(): boolean {
        return this.formForVideo.result instanceof ResultValue;
    }

    @computed public get isEditMode(): boolean {
        return this.page === 'edit' || this.isActiveFlagChanged;
    }

    @computed public get selectedImg(): string | null {
        return this.imgUrl.value === '' ? null : this.imgUrl.value;
    }

    public selectFilterVideo = (): void => {
        this.selectedNotificationPageType = 'video';
    };

    public selectFilterImage = (): void => {
        this.selectedNotificationPageType = 'image';
    };

    @computed private get parsedStartDate(): number | null {
        return this.dateFrom.value !== null ? Date.parse(this.dateFrom.value) : null;
    }

    @computed private get parsedEndDate(): number | null {
        return this.dateTo.value !== null ? Date.parse(this.dateTo.value) : null;
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }

    onChangeActionTemplateCB = (): void => {
        this.ctaUrlModel.reset();
        this.eventIdModel.reset();
        this.competitionIdModel.reset();
        this.casinoGameIdModel.reset();
        this.sportModel.reset();
    };

    @action moveItemUp = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'up',
        };

        const result = await apiCommon.post_order_promo_notifications.run({ data });

        if (result) {
            await this.promoNotificationsResource.refresh();
            this.resetForm();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @action moveItemDown = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'down',
        };

        const result = await apiCommon.post_order_promo_notifications.run({ data });

        if (result) {
            await this.promoNotificationsResource.refresh();
            this.resetForm();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    static createForContext(): NotificationPageState {
        return new NotificationPageState();
    }
}
